import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { Modal, Button, Form } from 'react-bootstrap'

import { 
  getSharedCustomerDetail,
  customerShareVerifyEmail,
  customerShareVerifyCode,
} from 'src/main_app/services/apis'
import { getUrlParameter } from 'src/main_app/utils/config-utility'
import SharedUsageCharts from "src/main_app/components/shared-usage-charts"

const SharedCustomerSimpleView = (props) => {
  const { customerId } = useParams()
  const { addToast } = useToasts()
  const [customer, setCustomer] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showVerifyModal, setShowVerifyModal] = useState(true)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [step, setStep] = useState('email') // 'email' | 'code' | 'done'

  const fetchCustomerData = async (customerIdValue, { email, shareCode }) => {
    const response = await getSharedCustomerDetail(customerIdValue, { email, shareCode })

    let customerData = null
    if (response.data && response.data.data) {
      customerData = response.data.data
    } else if (response.data) {
      customerData = response.data
    } else {
      customerData = response
    }

    setCustomer(customerData)
    setError(null)
  }

  useEffect(() => {
    if (!customerId) {
      setError('Invalid share link. Missing customer ID.')
      setShowVerifyModal(false)
      return
    }

    const emailFromUrl = getUrlParameter('email')
    const shareCodeFromUrl = getUrlParameter('shareCode')

    // If email & shareCode are present in URL, skip modal and load data directly
    if (emailFromUrl && shareCodeFromUrl) {
      setEmail(emailFromUrl)
      setStep('done')
      setShowVerifyModal(false)
      setIsLoading(true)

      fetchCustomerData(customerId, { email: emailFromUrl, shareCode: shareCodeFromUrl })
        .catch((err) => {
          console.error('Error fetching shared customer data:', err)
          setError('Failed to load customer details. Please try again or request a new share link.')
        })
        .finally(() => {
          setIsLoading(false)
        })
      return
    }

    // No email/shareCode in URL → require verification flow
    setShowVerifyModal(true)
  }, [customerId])

  const handleRequestCode = async (event) => {
    event.preventDefault()

    if (!email) {
      setEmailError('Please enter your email.')
      return
    }

    setEmailError('')

    try {
      setIsLoading(true)
      await customerShareVerifyEmail(customerId, email)

      setStep('code')
      addToast('Verification code has been sent to your email.', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (error) {
      console.error('Error requesting verification code:', error)

      if (error.response?.status === 404) {
        setError('Share link not found or has expired.')
      } else if (error.response?.status === 403) {
        setError('Email does not match any recipient for this share link.')
      } else {
        setError('Could not send verification code. Please try again.')
      }

      addToast('Failed to send verification code', {
        appearance: 'error',
        autoDismiss: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async (event) => {
    event.preventDefault()

    if (!code) {
      setCodeError('Please enter the verification code.')
      return
    }

    setCodeError('')

    try {
      setIsLoading(true)
      await customerShareVerifyCode(customerId, { email, shareCode: code })

      const baseUrl = window.location.origin
      const redirectUrl = `${baseUrl}/shared-customer/${customerId}?email=${encodeURIComponent(
        email
      )}&shareCode=${encodeURIComponent(code)}`

      window.location.href = redirectUrl
    } catch (error) {
      console.error('Error verifying code:', error)

      if (error.response?.status === 404) {
        setError('Customer not found or code has expired.')
      } else if (error.response?.status === 403) {
        setError('Invalid verification code. Please check and try again.')
      } else {
        setError('Failed to verify access. Please try again.')
      }

      addToast('Failed to verify access', {
        appearance: 'error',
        autoDismiss: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const nameOfCustomer = (customerObj) => {
    if (customerObj.suburb_name) {
      return `${customerObj.address}, ${customerObj.suburb_name} Installation ID ${customerObj.installation_id}`
    }
    return customerObj.name || customerObj.trading_name || 'Unknown Customer'
  }

  if (isLoading && !customer?.id) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="sr-only">Loading customer details...</span>
            </div>
            <p>Loading customer details...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
          <div className="text-center">
            <div className="alert alert-danger" role="alert" style={{ maxWidth: '500px' }}>
              <i className="fas fa-exclamation-triangle mb-2" style={{ fontSize: '2rem' }}></i>
              <h4>Access Denied</h4>
              <p>{error}</p>
              <button 
                className="btn btn-primary mt-3" 
                onClick={() => window.location.href = '/sign-in'}
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container-fluid" style={{ padding: '20px' }}>
      <Modal show={showVerifyModal} backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Verify your access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {step === 'email' && (
            <Form onSubmit={handleRequestCode}>
              <p className="mb-3">
                Please enter your email address above, and if it is a valid email address you will receive a code that will provide access to Aquo.
              </p>
              <Form.Group controlId="sharedCustomerEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isInvalid={!!emailError}
                  disabled={isLoading}
                />
                <Form.Control.Feedback type="invalid">
                  {emailError}
                </Form.Control.Feedback>
              </Form.Group>
              <Button
                variant="primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Sending code...' : 'Send verification code'}
              </Button>
            </Form>
          )}

          {step === 'code' && (
            <Form onSubmit={handleVerifyCode}>
              <p className="mb-3">
                We have sent a verification code to <strong>{email}</strong>. Enter it below to access the customer details.
              </p>
              <Form.Group controlId="sharedCustomerCode">
                <Form.Label>Verification code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter the code from your email"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  isInvalid={!!codeError}
                  disabled={isLoading}
                />
                <Form.Control.Feedback type="invalid">
                  {codeError}
                </Form.Control.Feedback>
              </Form.Group>
              <Button
                variant="primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify and continue'}
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>
      {/* Header */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h2 className="mb-0">
            {nameOfCustomer(customer)}
          </h2>
        </div>
        
        {/* Customer Basic Info */}
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Customer Information</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <p><strong>Name:</strong> {customer.name || 'N/A'}</p>
                <p><strong>Trading Name:</strong> {customer.trading_name || 'N/A'}</p>
                <p><strong>Address:</strong> {customer.address || 'N/A'}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Installation ID:</strong> {customer.installation_id || 'N/A'}</p>
                <p><strong>Account Number:</strong> {customer.account_number || 'N/A'}</p>
                <p><strong>Suburb:</strong> {customer.suburb_name || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Charts */}
      <SharedUsageCharts
        customerId={customerId}
        customer={customer}
      />
    </div>
  )
}

export default SharedCustomerSimpleView